/* eslint-disable no-restricted-imports */
import React, {createContext, useContext, useState, useEffect} from 'react'
import {useHotkeys} from 'react-hotkeys-hook'
import {CA} from '../../../../../../code/client/shared/scripts/analytics/Analytics'
import {AnalyticEventCategory, AnalyticEventType, AnalyticEventValue} from '../../../../../../code/shared/entity_datas/EntityDatas'
import {ClientUserState} from '../../../../../../code/client/shared/scripts/ClientUserState'
import {ViewPopup} from '../../../../../../code/client/shared/scripts/ui_popup/ViewPopup'
import {createDivIn, createImgIn} from '../../../../../../code/client/shared/scripts/ui_utils/DomUtils'
import {navigateToPage} from '../../../../../../code/client/shared/scripts/ui_utils/ClientUtils'
import {LobbyImage} from '@colonist/types'
import {TranslationObject} from '@colonist/i18n'
import {ViewPopupOptions} from '../../../../../../code/client/shared/scripts/ui_popup/ViewPopupOptions'
import {StorageWrapper} from '../../../utils/storage'

interface UIGameFullScreenContextProps {
    isFullScreen: boolean
    toggleFullScreen: () => void
}

const UIGameFullScreenContext = createContext<UIGameFullScreenContextProps | undefined>(undefined)

export const useUIGameFullScreenContext = () => {
    const context = useContext(UIGameFullScreenContext)
    if(!context) throw new Error('useUIGameFullScreenContext must be used within a UIGameFullScreenContext')
    return context
}

export const UIGameFullScreenProvider = ({children}: {children: React.ReactNode}) => {
    const [isFullScreen, setIsFullScreen] = useState(!!document.fullscreenElement)

    useEffect(() => {
        const onFullScreenChange = () => {
            setIsFullScreen(!!document.fullscreenElement)
        }

        document.addEventListener('fullscreenchange', onFullScreenChange)
        return () => {
            document.removeEventListener('fullscreenchange', onFullScreenChange)
        }
    }, [])

    useHotkeys('f', () => {
        if(!StorageWrapper.getKeyboardShortcuts()) return
        toggleFullScreen()
    })

    const sendFullScreenAnalyticEvent = () => {
        void CA.sendAnalyticEvent({
            type: AnalyticEventType.ABTestClickAnalytics,
            category: AnalyticEventCategory.ABTestClickAnalytics,
            floatValue: AnalyticEventValue.PopupMembershipIncentiveFullScreen,
        })
    }

    const showFullScreenMembershipPopup = () => {
        const POPUP_TITLE: TranslationObject = {key: 'strings:game.fullScreen.title'}
        const POPUP_BODY: TranslationObject = {key: 'strings:game.fullScreen.body'}
        const POPUP_OPTIONS: ViewPopupOptions = {
            showCheckButton: true,
            showXButton: false,
            checkButtonCustomContent: {key: 'strings:game.fullScreen.checkButton'},
        }
        const fullScreenPopupCheckActions = () => {
            navigateToPage('/store#membership', true)
        }
        const popup = new ViewPopup(POPUP_TITLE, POPUP_BODY, fullScreenPopupCheckActions, () => undefined, true, POPUP_OPTIONS)
        const fullscreenImgContainer = createDivIn(popup.body, 'popup_fullscreen_img_container')
        createImgIn(fullscreenImgContainer, LobbyImage.FullScreenExpand, 'popup_fullscreen_img')

        popup.body.style.flexDirection = 'column'
        popup.body.style.alignItems = 'center'
        popup.checkButton.classList.remove('btn_general')
        popup.checkButton.classList.add('popup_adblock_cta_btn')
        popup.show()
    }

    const requestFullScreen = async() => {
        sendFullScreenAnalyticEvent()

        const isMember = ClientUserState.userState?.items.hasMembership()
        if(!isMember) {
            showFullScreenMembershipPopup()
            return
        }

        await document.documentElement.requestFullscreen()
    }

    const exitFullScreen = async() => {
        sendFullScreenAnalyticEvent()

        await document.exitFullscreen()
    }

    const toggleFullScreen = () => {
        if(isFullScreen) {
            void exitFullScreen()
        } else {
            void requestFullScreen()
        }
    }

    return (
        <UIGameFullScreenContext.Provider value={{
            isFullScreen: isFullScreen,
            toggleFullScreen: toggleFullScreen,
        }}>
            {children}
        </UIGameFullScreenContext.Provider>
    )
}
